import { Field, Form, useFormikContext } from "formik";
import React, { FC, useEffect } from "react";
import { CertificateAction, CertificateActionType } from "../../constants/certificateActions";
import { EncryptionTypes } from "../../constants/encryptionTypes";
import { useLocale } from "../../contexts/localeContext";
import { TranslationKeys } from "../../contexts/TranslationKeys";
import closeIcon from '../../images/close.svg';
import { VALIDITY_IN_OPTION } from "../../models/CertificateType";
import { ListQuery } from "../../models/ListQuery";
import { MultiSelectOption } from "../../models/MultiSelectOption";
import { getCertificateTypes, getValidCertificateAuthorities } from "../../services/securityPolicyService";
import { startsWithZero } from "../../utilities/regex";
import { internetProtocolListValidation, requiredValidation, uniformResourceIdentifierListValidation, validateValidityInOption } from "../../utilities/Validation";
import Label from "../label/label";
import PaginatedSearchableSelectField from "../selection-field/paginated-searchable-select-field";

interface GenerateRecreateCertificateCardProps {
    type: CertificateActionType;
    title: string;
    onClose: () => void;
}

enum ValidityFor {
    ADD = 'ADD',
    SUBTRACT = 'SUBTRACT'
}

const GenerateRecreateCertificateCard: FC<GenerateRecreateCertificateCardProps> = ({ type, title,  onClose }) => {
    const { translate } = useLocale();
    const { values, errors, setFieldValue } = useFormikContext<any>();

    const validityInOptions: MultiSelectOption[] = [
        { value: VALIDITY_IN_OPTION.YEARS, label: translate.byKey(TranslationKeys.years) },
        { value: VALIDITY_IN_OPTION.DAYS, label: translate.byKey(TranslationKeys.days) }
    ];

    const onValidityInChanged = (option: MultiSelectOption) => {
        setFieldValue("validityIn", option.value);
    }

    const handleValidityForChange = (type: ValidityFor) => {
        if (type === ValidityFor.ADD) {
            setFieldValue("validFor", Number(values.validFor) + 1);
        } else if (type === ValidityFor.SUBTRACT) {
            setFieldValue("validFor", Number(values.validFor) - 1);
        }
    }

    const handleNumericInputChange = (e: React.ChangeEvent<HTMLInputElement>, form: any) => {
        const inputValue = e.target.value;
        const numericValue = inputValue.replace(/[^0-9]/g, "");

        if (numericValue && !startsWithZero.test(numericValue)) {
            setFieldValue("validFor", numericValue);
        } else if (inputValue && (!numericValue || startsWithZero.test(numericValue))) {
            // Reset to previous valid value or default
            setFieldValue("validFor", "1");
        }
    }

    const handleEncryptionTypeChange = (encryptionType:EncryptionTypes) => {
        setFieldValue("encryptionType", encryptionType);
        setFieldValue("keyParameter", "");
    }

    /**
     * Get key parameter options based on the selected encryption type
     * @returns Array of options for RSA key sizes or EC curves
     */
    const getKeyParameterOptions = async () => {
        if (values.encryptionType === EncryptionTypes.RSA) {
            // RSA key sizes in bits - larger sizes provide stronger security but slower performance
            const options =  [
                {value:"1024",label:"1024"}, // Minimum recommended size (legacy support)
                {value:"2048",label:"2048"}, // Most commonly used, good balance of security and performance
                {value:"4096",label:"4096"} // High security, slower performance
            ]
            return {
                rows: options,
                pagination: { maxPageSize: 10, totalCount: options.length, pageNumber: 1, totalPages: 1 }
            };
        }else if (values.encryptionType === EncryptionTypes.EC) {
            // Elliptic Curve parameters - different curves offer varying levels of security
            const options = [
                {value:"P-256", label:"P-256"}, // NIST P-256 curve, equivalent to 3072-bit RSA
                {value:"P-384", label:"P-384"}, // NIST P-384 curve, equivalent to 7680-bit RSA
                {value:"P-521", label:"P-521"}, // NIST P-521 curve, equivalent to 15360-bit RSA
            ]
            return {
                rows: options,
                pagination: { maxPageSize: 10, totalCount: options.length, pageNumber: 1, totalPages: 1 }
            };
        }
        return {
            rows: [],
            pagination: { maxPageSize: 10, totalCount: 0, pageNumber: 1, totalPages: 0 }
        };
    }

    const getShaAlgorithmOptions = async () => {
        const options =  [
            { value: "SHA256", label: "SHA256" },
            { value: "SHA384", label: "SHA384" },
            { value: "SHA512", label: "SHA512" }
        ];
        return {
            rows: options,
            pagination: { maxPageSize: 10, totalCount: options.length, pageNumber: 1, totalPages: 1 }
        };
    };

    const handleCaCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue("isCA", e.target.checked);
        if (e.target.checked) {
            setFieldValue("certificateAuthorityId", "");
            setFieldValue("certificateAuthorityLabel", "");
        } else {
            setFieldValue("certificateTypeId", "");
            setFieldValue("certificateTypeLabel", "");
        }
    };

    // Set default encryption type to RSA if not already set
    useEffect(() => {
        if (!values.encryptionType) {
            setFieldValue("encryptionType", EncryptionTypes.RSA);
        }
    }, [values.encryptionType, setFieldValue]);

    return (
        <Form>
            <div className={`w-full p-2 border-b flex flex-row items-center bg-sidemenu-focus text-white justify-between`}>
                <div className="flex flex-row">
                    <p className="pl-3 font-bold font text-white">{title}</p>
                </div>
                <img src={closeIcon} onClick={onClose} className="cursor-pointer mr-2" />
            </div>
            <div className="pt-3 pl-8 pr-0" style={{maxHeight:'70vh', overflowY:'auto', width:'400px'}} >
                <div className='flex flex-col space-y-3'>
                    
                        <div className="flex flex-row mb-2 mr-6">
                            <div className="flex flex-col flex-1 pr-3">
                                <div className="flex flex-row justify-between">
                                    <Label
                                        htmlFor="certificateType"
                                        className="text-xs text-black mt-1">
                                        {translate.byKey(TranslationKeys.certificateType)}:
                                    </Label>
                                    {errors.certificateType ? <span className='text-red-600 text-xs'>{errors.certificateType}</span> : null}
                                </div>
                                <div id="certificateTypeDropDownField">
                                    <PaginatedSearchableSelectField
                                        name="Certificate Type"
                                        accessor='certificateTypeId'
                                        key={"certificateTypeSelectField"}
                                        dataMethod={(listParameters?: ListQuery) => getCertificateTypes(listParameters)}
                                        searchAccessor={'trnName'}
                                        required={false}
                                        onSelect={(_, item) => {
                                            setFieldValue('certificateTypeLabel', item?.label);
                                            setFieldValue('certificateTypeId', item?.value);
                                        }}
                                        selectedItemLabel={values.certificateTypeLabel} />
                                </div>
                            </div>
                        </div>
                    <div className="flex flex-row mb-2 mr-6">
                        <div className="flex flex-col flex-1 pr-3">
                            <div className="flex flex-row justify-between" id='aliasLabelId'>
                                <Label
                                    htmlFor="alias"
                                    className="text-xs text-black mt-1"
                                    required>{translate.byKey(TranslationKeys.alias)}:</Label>
                                {errors.alias ? <span className='text-red-600 text-xs'>{errors.alias}</span> : null}
                            </div>
                            <Field id='aliasInputFieldId'
                                   type="text"
                                   name="alias"
                                   disabled={type === CertificateAction.RECREATE}
                                   autoFocus
                                   className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                                   validate={(value) => requiredValidation(value, translate)}/>
                        </div>
                    </div>

                    <div className="flex flex-row mb-2 mr-6">
                        <div className="flex flex-col flex-1 pr-3">
                            <div className="flex flex-row justify-between" id='commonName'>
                                <Label htmlFor='commonName'
                                       className="text-xs text-black mt-1"
                                       required>{translate.byKey(TranslationKeys.commonName)}:</Label>
                                {errors.commonName ? <span className='text-red-600 text-xs'>{errors.commonName}</span> : null}
                            </div>
                            <Field id='commonNameInputFieldId'
                                   type="text"
                                   name="commonName"
                                   disabled={type === CertificateAction.RECREATE}
                                   className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                                   validate={(value) => requiredValidation(value, translate)}/>
                        </div>
                    </div>

                    <div className="flex flex-row mb-2 mr-6">
                        <div className="flex flex-col flex-1 pr-3">
                            <div className="flex flex-row justify-between" id='lblCertificateValidity'>
                                <Label
                                    htmlFor="lblCertificateValidity"
                                    className="text-sm text-portal-blue-primary-shade2 mt-1 font-bold"
                                    >{translate.byKey(TranslationKeys.certificateValidity)}:</Label>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row mb-2 mr-6">
                        <div className="flex flex-col flex-1 pr-3">
                            <div className="flex flex-row justify-between">
                                <Label
                                    htmlFor="validityIn"
                                    className="text-xs text-black mt-1"
                                    required>{translate.byKey(TranslationKeys.validityIn)}:</Label>
                                {errors.validityIn ? <span className='text-red-600 text-xs'>{errors.validityIn}</span> : null}
                            </div>
                            <div className='flex flex-row space-x-4'>
                                {validityInOptions.map((option) => (
                                    <div
                                        key={option.value}
                                        className="flex items-center text-sm font-medium ">
                                        <Field
                                            id={`radio-${option.value}`}
                                            type="radio"
                                            name="validityIn"
                                            value={option.value}
                                            validate={(value) => validateValidityInOption(value, translate)}
                                            onChange={(_: any) => onValidityInChanged(option)}
                                        />
                                        <Label htmlFor={`radio-${option.value}`} className="pl-1 text-sm">{option.label}</Label>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row mb-2 mr-6">
                        <div className="flex flex-col flex-1 pr-3">
                            <div className="flex flex-row justify-between" id='validFor'>
                                <Label htmlFor='validFor'
                                       className="text-xs text-black mt-1 mb-1"
                                       required>{translate.byKey(TranslationKeys.duration)}:</Label>
                                {errors.validFor ? <span className='text-red-600 text-xs'>{errors.validFor}</span> : null}
                            </div>
                            <div className='flex flex-row space-x-2 h-6'>
                                <button type={'button'}
                                        onClick={() => handleValidityForChange(ValidityFor.SUBTRACT)}
                                        disabled={values.validFor < 2}
                                        className="flex flex-row text-portal-blue-primary justify-center items-center rounded-md border border-solid border-gray-400 w-6 text-center pr-2 pl-2 p-1 text-sm bg-gray-input">
                                    -
                                </button>
                                <Field
                                    name="validFor"
                                    id="validityForInputFieldId"
                                    inputMode="numeric"
                                    pattern="[1-9][0-9]*"
                                    className="flex flex-row items-center w-12 pr-2 pl-2 p-1 text-gray-800 text-sm bg-gray-input text-center"
                                    onChange={handleNumericInputChange}
                                    validate={(value) => requiredValidation(value, translate)}/>
                                <button type={'button'}
                                        onClick={() => handleValidityForChange(ValidityFor.ADD)}
                                        className="flex flex-row text-portal-blue-primary justify-center items-center rounded-md border border-solid border-gray-400 w-6 text-center pr-2 pl-2 p-1 text-sm bg-gray-input">
                                    +
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-row mb-2 mr-6">
                        <div className="flex flex-col flex-1 pr-3">
                            <div className="flex flex-row justify-between" id='aliasLabelId'>
                                <Label
                                    htmlFor="alias"
                                    className="text-sm text-portal-blue-primary-shade2 mt-1 font-bold"
                                    >{translate.byKey(TranslationKeys.encryption)}:</Label>
                                {errors.alias ? <span className='text-red-600 text-xs'>{errors.alias}</span> : null}
                            </div>
                        </div>
                    </div>
                    {/*New Encryption Type Section*/}
                    <div className="flex flex-row mb-2 mr-6">
                        <div className="flex flex-col flex-1 pr-3">
                            <div className="flex flex-row justify-between">
                                <Label htmlFor="encryptionType"
                                       className="text-xs text-black mt-1 mb-1"
                                       required>{translate.byKey(TranslationKeys.encryptionType)}:</Label>
                                {errors.encryptionType ? <span className='text-red-600 text-xs'>{errors.encryptionType}</span> : null}
                            </div>
                            <div className="flex flex-row space-x-4">
                                <div className="flex items-center text-sm font-medium">
                                    <Field
                                        id="rsa-checkbox"
                                        type="checkbox"
                                        name="encryptionType"
                                        value={EncryptionTypes.RSA}
                                        checked={values.encryptionType === EncryptionTypes.RSA}
                                        onChange={() => handleEncryptionTypeChange(EncryptionTypes.RSA)}
                                        className="mr-1"/>
                                    <Label htmlFor="rsa-checkbox" className="pl-1 text-sm">{translate.byKey(TranslationKeys.rsa)}</Label>
                                </div>
                                <div className="flex items-center text-sm font-medium">
                                    <Field
                                        id="ec-checkbox"
                                        type="checkbox"
                                        name="encryptionType"
                                        value={EncryptionTypes.EC}
                                        checked={values.encryptionType === EncryptionTypes.EC}
                                        onChange={() => handleEncryptionTypeChange(EncryptionTypes.EC)}
                                        className="mr-1"/>
                                    <Label htmlFor="ec-checkbox" className="pl-1 text-sm">{translate.byKey(TranslationKeys.ecParam)}</Label>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/*Key Parameter Dropdown*/}
                    {values.encryptionType && (
                        <div className="flex flex-row mb-2 mr-6">
                            <div className="flex flex-col flex-1 pr-3">
                                <div className="flex flex-row justify-between">
                                    <Label
                                        htmlFor="keyParameter"
                                        className="text-xs text-black mt-1 mb-1" required>
                                        {values.encryptionType === EncryptionTypes.RSA ? translate.byKey(TranslationKeys.keySize) : translate.byKey(TranslationKeys.curve)}
                                    </Label>
                                    {errors.keyParameter ? <span className='text-red-600 text-xs'>{errors.keyParameter}</span> : null}
                                </div>
                                <PaginatedSearchableSelectField
                                    name={values.encryptionType === EncryptionTypes.RSA ? "Key Size" : "Curve"}
                                    accessor='keyParameter'
                                    key={`keyParameter-${values.encryptionType}`} // Key changes when encryption type changes
                                    dataMethod={getKeyParameterOptions}
                                    searchAccessor={'label'}
                                    required={true}
                                    disabled={false}
                                    onSelect={(_, item) => {
                                        setFieldValue('keyParameterLabel', item?.label);
                                        setFieldValue('keyParameter', item?.value);
                                    }}
                                    selectedItemLabel={values.keyParameterLabel}
                                />
                            </div>
                        </div>
                    )}
                    {/*SHA Algorithm Dropdown*/}
                    <div className="flex flex-row mb-2 mr-6">
                        <div className="flex flex-col flex-1 pr-3">
                            <div className="flex flex-row justify-between">
                                <Label
                                    htmlFor="shaAlgorithm"
                                    className="text-xs text-black mt-1 mb-1" required>
                                    {translate.byKey(TranslationKeys.shaAlgorithm)}
                                </Label>
                                {errors.shaAlgorithm ? <span className='text-red-600 text-xs'>{errors.shaAlgorithm}</span> : null}
                            </div>
                            <PaginatedSearchableSelectField
                                name="SHA Algorithm"
                                accessor='shaAlgorithm'
                                key="shaAlgorithmSelectField"
                                dataMethod={getShaAlgorithmOptions}
                                searchAccessor={'label'}
                                required={true}
                                disabled={false}
                                onSelect={(_, item) => {
                                    setFieldValue('shaAlgorithmLabel', item?.label);
                                    setFieldValue('shaAlgorithm', item?.value);
                                }}
                                selectedItemLabel={values.shaAlgorithmLabel}
                            />
                        </div>
                    </div>
                    {/*CA Checkbox Section*/}
                    <div className="flex flex-row mb-2 mr-6">
                        <div className="flex flex-col flex-1 pr-3">
                            <div className="flex flex-row justify-between">
                                <Label htmlFor="isCA" className="text-sm text-portal-blue-primary-shade2 mt-1 font-bold">
                                    {translate.byKey(TranslationKeys.ca)}:
                                </Label>
                                {errors.isCA ? <span className='text-red-600 text-xs'>{errors.isCA}</span> : null}
                            </div>
                            <div className="flex items-center text-sm font-medium">
                                <Field
                                    id="ca-checkbox"
                                    type="checkbox"
                                    name="isCA"
                                    checked={values.isCA || false}
                                    onChange={handleCaCheckboxChange}
                                    className="mr-1"/>
                                <Label htmlFor="ca-checkbox" className="pl-1 text-sm">{translate.byKey(TranslationKeys.generateAsCertificateAuthority)}</Label>
                            </div>
                        </div>
                    </div>
                    {!values.isCA ? (
                        // Certificate Authority Dropdown
                        <div className="flex flex-row mb-2 mr-6">
                            <div className="flex flex-col flex-1 pr-3">
                                <div className="flex flex-row justify-between">
                                    <Label
                                        htmlFor="certificateAuthority"
                                        className="text-xs text-black mt-1" required>
                                        {translate.byKey(TranslationKeys.certificateAuthority)}:
                                    </Label>
                                    {errors.certificateAuthorityId ? <span className='text-red-600 text-xs'>{errors.certificateAuthorityId}</span> : null}
                                </div>
                                <div id="certificateAuthorityDropDownField">
                                    <PaginatedSearchableSelectField
                                        name="Certificate Authority"
                                        accessor='certificateAuthorityId'
                                        key={"certificateAuthoritySelectField"}
                                        dataMethod={(listParameters?: ListQuery) => getValidCertificateAuthorities(listParameters)}
                                        searchAccessor={'name'}
                                        required={false}
                                        disabled={values.isCA || false}
                                        onSelect={(_, item) => {
                                            setFieldValue('certificateAuthorityLabel', item?.label);
                                            setFieldValue('certificateAuthorityId', item?.value);
                                        }}
                                        selectedItemLabel={values.certificateAuthorityLabel} />
                                </div>
                            </div>
                        </div>
                    ) : null}
                    
                    <div className="flex flex-row mb-2 mr-6">
                        <div className="flex flex-col flex-1 pr-3">
                            <div className="flex flex-row justify-between mt-1" id='subjectAlternativeName'>
                                <Label htmlFor='subjectAlternativeName'
                                       className="text-sm text-portal-blue-primary-shade2 mt-1 font-bold">{translate.byKey(TranslationKeys.subjectAlternativeName)}:</Label>
                            </div>
                            <div className="flex flex-row justify-between mt-1" id='domainNameSystem'>
                                <Label htmlFor='domainNameSystem'
                                       required
                                       className="text-xs text-black mt-1 mb-1">{translate.byKey(TranslationKeys.domainNameSystem)}:</Label>
                            </div>
                            <Field id='domainNameSystemInputFieldId'
                                   type="text"
                                   name="domainNameSystem"
                                   disabled={type === CertificateAction.RECREATE}
                                   className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                                   validate={(value) => requiredValidation(value, translate)}/>
                            {errors.domainNameSystem ? <span className='text-red-600 text-xs'>{errors.domainNameSystem}</span> : null}
                            <div className="flex flex-row justify-between mt-1" id='internetProtocol'>
                                <Label htmlFor='internetProtocol'
                                       className="text-xs text-black mt-1 mb-1">{translate.byKey(TranslationKeys.internetProtocol)}:</Label>
                            </div>
                            <Field id='internetProtocolInputFieldId'
                                   type="text"
                                   name="internetProtocol"
                                   disabled={type === CertificateAction.RECREATE}
                                   className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                                   validate={(value) => internetProtocolListValidation(value, true, translate)} />
                            {errors.internetProtocol ? <span className='text-red-600 text-xs'>{errors.internetProtocol}</span> : null}
                            <div className="flex flex-row justify-between mt-1" id='uniformResourceIdentifier'>
                                <Label htmlFor='uniformResourceIdentifier'
                                       className="text-xs text-black mt-1 mb-1">{translate.byKey(TranslationKeys.uniformResourceIdentifier)}:</Label>
                            </div>
                            <Field id='uniformResourceIdentifierInputFieldId'
                                   type="text"
                                   name="uniformResourceIdentifier"
                                   disabled={type === CertificateAction.RECREATE}
                                   className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                                   validate={(value) => uniformResourceIdentifierListValidation(value, translate)}/>
                            {errors.uniformResourceIdentifier ? <span className='text-red-600 text-xs'>{errors.uniformResourceIdentifier}</span> : null}
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-row pb-3 pr-3 mt-4 mr-5 space-x-2 justify-end bg-white">
                <button className="py-1 px-5 secondary secondary-inverse" type="button" onClick={onClose}>
                    {translate.byKey(TranslationKeys.cancel)}
                </button>
                <button
                    id='submitBtn'
                    className="secondary secondary-enabled py-1 px-5"
                    type='submit'>
                    {translate.byKey(TranslationKeys.generate)}
                </button>
            </div>
        </Form>
    )
}

export default GenerateRecreateCertificateCard;
